import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowLeft, Loader2, Award } from "lucide-react";
import Seo from "../components/Seo";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * LegendaryWallPage — public alumni of every VIN that has ever earned
 * EVIQ Legendary Preservation™. Anonymized to last 4 of VIN per user
 * requirement. Sorted by best trust score DESC, ties broken by
 * first_awarded_at ASC (OG legends stay on top).
 */
export default function LegendaryWallPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/battery/legendary_wall?limit=500`)
      .then((r) => (r.ok ? r.json() : { entries: [] }))
      .then((d) => setRows(d.entries || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="legendary-wall-page">
      <Seo
        title="Legendary Preservation Hall — Tesla battery legends"
        description="The hall of Tesla owners who have preserved their battery health at legendary levels — every VIN that has ever earned EVIQ Legendary Preservation, ranked by verified Trust Score."
        canonical="https://eviq.pro/legendary"
      />
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Link
          to="/"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5 mb-6"
          data-testid="legendary-wall-back"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={2.4} />
          EVIQ
        </Link>

        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2 inline-flex items-center gap-2">
            <Trophy className="w-3 h-3" style={{ color: "#FFB020" }} strokeWidth={2.4} />
            A flat degradation slope, held for 90 days
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold leading-tight">
            The <span style={{
              background: "linear-gradient(135deg,#FFB020,#FF6B57)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>legends</span> hall.
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed mt-3 max-w-xl">
            Every EV pack that has held a flat degradation slope — a score of{" "}
            <span className="text-white font-semibold">95 or higher</span> (the
            Legendary Preservation™ badge) — for at least{" "}
            <span className="text-white font-semibold">90 continuous days</span>.
            Anonymized to last-4 of VIN. If your VIN's on this list —
            <span className="text-white"> you're preserving your pack better than 99% of owners.</span>
          </p>
        </div>

        {loading ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-zinc-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.2} />
            Loading legends…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center" data-testid="legendary-wall-empty">
            <Trophy className="w-8 h-8 mx-auto mb-3" style={{ color: "#FFB020" }} strokeWidth={2.2} />
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] mb-2" style={{ color: "#FFB020" }}>
              Opening with the founding cohort
            </div>
            <div className="text-sm text-white font-semibold mb-1">The first legends are earning their 90 days right now.</div>
            <div className="text-[12px] text-zinc-400 leading-relaxed max-w-md mx-auto">
              The badge requires a signed Battery Passport held at 95+ for 90 continuous
              days — so by design, nobody can appear here on day one. It is auto-awarded
              server-side; no application, no paperwork. Connect your car and your clock starts today.
            </div>
          </div>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2" data-testid="legendary-wall-rows">
            {rows.map((r, i) => (
              <li
                key={`${r.vin_last4}-${i}`}
                className="rounded-xl border p-3 flex items-center gap-3"
                style={{
                  borderColor: "rgba(255,176,32,0.35)",
                  background: "linear-gradient(135deg, rgba(255,176,32,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                }}
                data-testid={`legendary-wall-row-${i}`}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center border shrink-0"
                  style={{
                    background: "rgba(255,176,32,0.14)",
                    borderColor: "rgba(255,176,32,0.6)",
                  }}
                >
                  <Trophy className="w-4 h-4" style={{ color: "#FFB020" }} strokeWidth={2.4} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-white font-semibold leading-tight">
                    {r.year || ""} {r.model || "Tesla"}
                    {r.trim ? <span className="text-zinc-400"> · {r.trim}</span> : null}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
                    …{r.vin_last4}
                    {r.first_awarded_at && (
                      <> · legend since {new Date(r.first_awarded_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center gap-1 justify-end">
                    <Award className="w-3 h-3" style={{ color: "#FFB020" }} strokeWidth={2.6} />
                    <span className="font-display text-lg font-semibold leading-none" style={{ color: "#FFB020" }}>
                      {r.best_trust_score ?? "—"}
                    </span>
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
                    best score
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {rows.length > 0 && (
          <div className="mt-6 text-center">
            <Link
              to="/leaderboard"
              className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5"
              data-testid="legendary-wall-see-full"
            >
              See the full leaderboard →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
